import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faDollarSign, 
  faUsers, 
  faCalendarAlt, 
  faDownload,
  faPlay,
  faPause,
  faUndo,
  faEye,
  faCheckCircle,
  faClock,
  faTimesCircle,
  faExclamationTriangle
} from "@fortawesome/free-solid-svg-icons";
import axiosHttp from "../../Config";
import Loader from "../../Components/Loader";
import { Chart, registerables } from 'chart.js';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Register Chart.js components
Chart.register(...registerables);

const Payments = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [payments, setPayments] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [overviewData, setOverviewData] = useState({
    monthlyRevenue: 0,
    subscriptionRevenue: 0, // Platform revenue from subscriptions
    sessionRevenue: 0, // Revenue from sessions (held for mentors)
    activeSubscriptions: 0,
    expiredSubscriptions: 0,
    upcomingRenewals: 0
  });
  const chartInstance = useRef(null);

  useEffect(() => {
    fetchOverviewData();
    fetchPaymentData();
    fetchMonthlyTrend();
  }, []);

  useEffect(() => {
    if (activeTab === "overview" && !loading && monthlyTrend.length) {
      initializeChart();
    }
  }, [activeTab, loading, monthlyTrend]);

  const initializeChart = () => {
    const ctx = document.getElementById('revenueChart');
    if (!ctx) return;

    if (!monthlyTrend.length) {
      return;
    }

    // Destroy existing chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: monthlyTrend.map(item => item.month),
        datasets: [
          {
            label: 'Total Revenue',
            data: monthlyTrend.map(item => item.totalRevenue),
            borderColor: '#4e73df',
            backgroundColor: 'rgba(78, 115, 223, 0.1)',
            borderWidth: 3,
            fill: true,
            tension: 0.4
          },
          {
            label: 'Subscription Revenue',
            data: monthlyTrend.map(item => item.subscriptionRevenue),
            borderColor: '#1cc88a',
            backgroundColor: 'rgba(28, 200, 138, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4
          },
          {
            label: 'Session Revenue',
            data: monthlyTrend.map(item => item.sessionRevenue),
            borderColor: '#f6c23e',
            backgroundColor: 'rgba(246, 194, 62, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
            labels: {
              usePointStyle: true,
              padding: 20
            }
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(context) {
                return context.dataset.label + ': ₹' + context.parsed.y.toLocaleString();
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return '₹' + value.toLocaleString();
              }
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.1)'
            }
          },
          x: {
            grid: {
              display: false
            }
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        }
      }
    });
  };

  // Normalizes aggregated numeric fields from various API shapes
  const pickNumberValue = (...values) => {
    for (const value of values) {
      if (value !== undefined && value !== null) {
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue : 0;
      }
    }
    return 0;
  };

  const fetchOverviewData = async () => {
    try {
      console.log('Payments - Fetching overview data...');
      const response = await axiosHttp.get('payments/overview');

      console.log('Payments - Overview API response:', response);

      if (response.status === 200) {
        const data = response.data?.data || response.data;
        console.log('Payments - Overview data:', data);

        setOverviewData({
          monthlyRevenue: pickNumberValue(
            data?.total_monthly_revenue,
            data?.totalMonthlyRevenue,
            data?.monthlyRevenue,
            data?.revenue,
            data?.totalRevenue
          ),
          subscriptionRevenue: pickNumberValue(
            data?.subscription_revenue,
            data?.subscriptionRevenue,
            data?.subscriptionIncome,
            data?.platformRevenue
          ),
          sessionRevenue: pickNumberValue(
            data?.session_revenue,
            data?.sessionRevenue,
            data?.sessionIncome,
            data?.mentorRevenue
          ),
          activeSubscriptions: pickNumberValue(
            data?.active_subscriptions,
            data?.activeSubscriptions,
            data?.activeUsers,
            data?.totalUsers
          ),
          expiredSubscriptions: pickNumberValue(
            data?.expired_subscriptions,
            data?.expiredSubscriptions,
            data?.inactiveUsers
          ),
          upcomingRenewals: pickNumberValue(
            data?.upcoming_renewals,
            data?.upcomingRenewals,
            data?.pendingRenewals
          )
        });
      }
    } catch (error) {
      console.error('Payments - Error fetching overview data:', error);
    }
  };


  const fetchMonthlyTrend = async () => {
    try {
      console.log('Payments - Fetching monthly trend data...');
      const response = await axiosHttp.get('payments/monthly-trend');

      console.log('Payments - Monthly trend API response:', response);

      if (response.status === 200) {
        const trendData = (response.data?.data || response.data || []).map((item, index) => ({
          month: item.month || item.month_name || item.label || `M${index + 1}`,
          totalRevenue: pickNumberValue(
            item.totalRevenue,
            item.total_revenue,
            item.revenue,
            item.total
          ),
          subscriptionRevenue: pickNumberValue(
            item.subscriptionRevenue,
            item.subscription_revenue,
            item.subscription_income,
            item.subscription
          ),
          sessionRevenue: pickNumberValue(
            item.sessionRevenue,
            item.session_revenue,
            item.session_income,
            item.session
          )
        }));
        setMonthlyTrend(trendData);
      }
    } catch (error) {
      console.error('Payments - Error fetching monthly trend data:', error);
    }
  };

  const fetchPaymentData = async () => {
    try {
      console.log('Payments - Fetching payment data...');
      
      // Fetch payment balance
      const balanceResponse = await axiosHttp.get('payment-balance');
      console.log('Payments - Payment balance response:', balanceResponse);
      
      // Fetch payment list
      const paymentsResponse = await axiosHttp.get('payments/details');
      console.log('Payments - Payments details response:', paymentsResponse);
      
      if (paymentsResponse.status === 200) {
        const rawPayments = paymentsResponse.data?.data || paymentsResponse.data || [];
        const paymentsData = Array.isArray(rawPayments)
          ? rawPayments
          : rawPayments.payments || rawPayments.data || rawPayments.items || [];
        console.log('Payments - Payments data:', paymentsData);
        setPayments(Array.isArray(paymentsData) ? paymentsData : []);
        
        // Extract payouts and refunds from payments data
        const payoutsData = paymentsData.filter(payment => 
          payment.payment_type === 'payout' || payment.type === 'payout'
        );
        const refundsData = paymentsData.filter(payment => 
          payment.payment_status === 'refunded' || payment.status === 'refunded'
        );
        
        setPayouts(payoutsData);
        setRefunds(refundsData);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Payments - Error fetching payment data:', error);
      setLoading(false);
    }
  };

  // const getStatusIcon = (status) => {
  //   switch (status?.toLowerCase()) {
  //     case 'paid':
  //     case 'completed':
  //     case 'c':
  //       return <FontAwesomeIcon icon={faCheckCircle} className="text-success" />;
  //     case 'hold':
  //     case 'h':
  //       return <FontAwesomeIcon icon={faPause} className="text-warning" />;
  //     case 'pending':
  //     case 'p':
  //       return <FontAwesomeIcon icon={faClock} className="text-warning" />;
  //     case 'refunded':
  //     case 'r':
  //       return <FontAwesomeIcon icon={faUndo} className="text-info" />;
  //     case 'failed':
  //     case 'f':
  //       return <FontAwesomeIcon icon={faTimesCircle} className="text-danger" />;
  //     default:
  //       return <FontAwesomeIcon icon={faExclamationTriangle} className="text-secondary" />;
  //   }
  // };
  // const getStatusBadge = (status) => {
  //   const statusClasses = {
  //     paid: "badge-success",
  //     completed: "badge-success",
  //     c: "badge-success",
  //     hold: "badge-warning",
  //     h: "badge-warning",
  //     pending: "badge-warning",
  //     p: "badge-warning",
  //     refunded: "badge-info",
  //     r: "badge-info",
  //     failed: "badge-danger",
  //     f: "badge-danger",
  //     processing: "badge-secondary"
  //   };
  //   return `badge ${statusClasses[status?.toLowerCase()] || 'badge-secondary'}`;
  // };
  const getStatusText = (status) => {
    const statusMap = {
      'h': 'Hold',
      'hold': 'Hold',
      'c': 'Completed',
      'p': 'Pending',
      'r': 'Refunded',
      'f': 'Failed'
    };
    return statusMap[status?.toLowerCase()] || status || 'Unknown';
  };
  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'completed':
      case 'c':
        return 'success';
      case 'hold':
      case 'h':
        return 'warning';
      case 'pending':
      case 'p':
        return 'warning';
      case 'refunded':
      case 'r':
        return 'info';
      case 'failed':
      case 'f':
        return 'danger';
      case 'cancelled':
      case 'cancelled':
        return 'danger';
      case 'no_show':
      case 'no_show':
        return 'warning';
      default:
        return 'secondary';
    }
  };
  const getPayoutStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'p':
      case 'pending':
        return 'warning';
      case 'c':
      case 'completed':
        return 'success';
      case 'f':
      case 'failed':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getPayoutStatus = (status) => {
    const statusMap = {
      'p': 'Pending',
      'pending': 'Pending',
      'c': 'Completed',
      'completed': 'Completed',
      'f': 'Failed',
      'failed': 'Failed'
    };
    return statusMap[status?.toLowerCase()] || status || 'Unknown';
  };
  const getRefundStatus = (status) => {
    const statusMap = {
      'p': 'Pending',
      'pending': 'Pending',
      'c': 'Completed',
      'completed': 'Completed',
      'r': 'Rejected',
      'rejected': 'Rejected'
    };
    return statusMap[status?.toLowerCase()] || status || 'Unknown';
  };
  const getRefundStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'p':
      case 'pending':
        return 'warning';
      case 'c':
      case 'completed':
        return 'success';
      case 'r':
      case 'rejected':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const exportToExcel = async () => {
    try {
      console.log('Payments - Downloading Excel from API...');

      const response = await axiosHttp.get('payments/details?download=1', {
        responseType: 'blob'
      });

      const disposition = response.headers?.['content-disposition'];
      let fileName = '';
      if (disposition) {
        const fileNameMatch = disposition.match(/filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i);
        if (fileNameMatch) {
          fileName = decodeURIComponent(fileNameMatch[1] || fileNameMatch[2] || fileNameMatch[3] || '').trim();
        }
      }

      if (!fileName) {
        const currentDate = new Date().toISOString().split('T')[0];
        fileName = `Payment_Details_${currentDate}.xlsx`;
      }

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, fileName);

      console.log('Payments - Excel download initiated:', fileName);
    } catch (error) {
      console.error('Payments - Error downloading Excel:', error);
    }
  };

  const exportPayoutsToExcel = () => {
    try {
      console.log('Expert Payouts - Exporting to Excel...');
      
      const excelData = payouts.map((payout, index) => ({
        'S.No': index + 1,
        'Mentor Name': payout.mentorName || payout.mentor_name || payout.expertName || payout.expert_name || 'N/A',
        'Mentor Account Number': payout.mentorAccountNumber || payout.mentor_account_number || 'N/A',
        'Total Sessions': payout.totalSessions || payout.total_sessions || 0,
        'Total Amount': payout.totalAmount || payout.total_amount || 0,
        'Payout Amount': payout.payoutAmount || payout.payout_amount || 0,
        'Status': getPayoutStatus(payout.payout_status || payout.status),
        'Last Payout Date': payout.lastPayoutDate || payout.last_payout_date || payout.updated_at || 'N/A',
        'Payment Method': payout.paymentMethod || payout.payment_method || 'N/A',
        'Bank Details': payout.bankDetails || payout.bank_details || 'N/A'
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      const columnWidths = [
        { wch: 5 },   // S.No
        { wch: 20 },  // Mentor Name
        { wch: 18 },  // Mentor Account Number
        { wch: 15 },  // Total Sessions
        { wch: 15 },  // Total Amount
        { wch: 15 },  // Payout Amount
        { wch: 12 },  // Status
        { wch: 18 },  // Last Payout Date
        { wch: 15 },  // Payment Method
        { wch: 25 }   // Bank Details
      ];
      worksheet['!cols'] = columnWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Expert Payouts');

      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `Expert_Payouts_${currentDate}.xlsx`;

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, fileName);

      console.log('Expert Payouts - Excel export completed:', fileName);
    } catch (error) {
      console.error('Expert Payouts - Error exporting to Excel:', error);
    }
  };

  const exportRefundsToExcel = () => {
    try {
      console.log('Refunds - Exporting to Excel...');
      
      const excelData = refunds.map((refund, index) => ({
        'S.No': index + 1,
        'Refund ID': refund.id || refund.refund_id || `REF-${index + 1}`,
        'Mentor Name': refund.mentorName || refund.mentor_name || refund.expertName || refund.expert_name || 'N/A',
        'Mentor Account Number': refund.mentorAccountNumber || refund.mentor_account_number || 'N/A',
        'Mentee Name': refund.menteeName || refund.mentee_name || refund.userName || refund.user_name || 'N/A',
        'Mentee Account Number': refund.menteeAccountNumber || refund.mentee_account_number || 'N/A',
        'Original Payment ID': refund.paymentId || refund.payment_id || 'N/A',
        'Refund Amount': refund.refundAmount || refund.refund_amount || 0,
        'Refund Reason': refund.refundReason || refund.refund_reason || refund.reason || 'N/A',
        'Status': getRefundStatus(refund.refund_status || refund.status),
        'Requested Date': refund.requestedDate || refund.requested_date || refund.created_at || 'N/A',
        'Processed Date': refund.processedDate || refund.processed_date || refund.updated_at || 'N/A',
        'Session Date': refund.sessionDate || refund.session_date || 'N/A',
        'No Show By': getNoShowBy(refund)
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      const columnWidths = [
        { wch: 5 },   // S.No
        { wch: 15 },  // Refund ID
        { wch: 20 },  // Mentor Name
        { wch: 18 },  // Mentor Account Number
        { wch: 20 },  // Mentee Name
        { wch: 18 },  // Mentee Account Number
        { wch: 18 },  // Original Payment ID
        { wch: 15 },  // Refund Amount
        { wch: 25 },  // Refund Reason
        { wch: 12 },  // Status
        { wch: 18 },  // Requested Date
        { wch: 18 },  // Processed Date
        { wch: 15 },  // Session Date
        { wch: 12 }   // No Show By
      ];
      worksheet['!cols'] = columnWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Refunds');

      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `Refunds_${currentDate}.xlsx`;

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, fileName);

      console.log('Refunds - Excel export completed:', fileName);
    } catch (error) {
      console.error('Refunds - Error exporting to Excel:', error);
    }
  };

  // const getTransactionStatus = (payment) => {
  //   const status = payment.payment_status || payment.status || payment.transaction_status;

  //   // Map status codes to readable text
  //   const statusMap = {
  //     'c': 'Completed',
  //     'completed': 'Completed',
  //     'h': 'Hold',
  //     'hold': 'Hold',
  //     'p': 'Pending',
  //     'pending': 'Pending',
  //     'r': 'Refunded',
  //     'refunded': 'Refunded',
  //     'f': 'Failed',
  //     'failed': 'Failed',
  //     'cancelled': 'Cancelled',
  //     'rescheduled': 'Rescheduled',
  //     'no_show': 'No Show'
  //   };

  //   return statusMap[status?.toLowerCase()] || status || 'Unknown';
  // };
  const getNoShowBy = (payment) => {
    const noShowBy = payment.noShowBy || payment.no_show_by || payment.absent_party;
    
    if (!noShowBy) return 'N/A';
    
    const noShowMap = {
      'mentor': 'Mentor',
      'mentee': 'Mentee',
      'both': 'Both'
    };

    return noShowMap[noShowBy.toLowerCase()] || noShowBy;
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="container-fluid top-container">
      <div className="card card-one-1 mb-5">
        <div className="card-body">
          <h4 className="title mb-4">Payments & Subscriptions</h4>

          {/* Tab Navigation */}
          <ul className="nav nav-tabs mb-4" id="paymentsTab" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "overview" ? "active" : ""}`}
                onClick={() => setActiveTab("overview")}
              >
                Overview
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "payments" ? "active" : ""}`}
                onClick={() => setActiveTab("payments")}
              >
                Payments
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "payouts" ? "active" : ""}`}
                onClick={() => setActiveTab("payouts")}
              >
                Expert Payouts
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "refunds" ? "active" : ""}`}
                onClick={() => setActiveTab("refunds")}
              >
                Refunds
              </button>
            </li>
          </ul>

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div>
              {/* Overview Cards */}
              <div className="row mb-4">
                <div className="col-xl-3 col-md-6 mb-4">
                  <div className="card border-left-primary shadow h-100 py-2">
                    <div className="card-body">
                      <div className="row no-gutters align-items-center">
                        <div className="col mr-2">
                          <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                            Total Monthly Revenue
                          </div>
                          <div className="h5 mb-0 font-weight-bold text-gray-800">
                            ₹{overviewData.monthlyRevenue.toLocaleString()}
                          </div>
                        </div>
                        <div className="col-auto">
                          <FontAwesomeIcon icon={faDollarSign} className="fa-2x text-gray-300" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-md-6 mb-4">
                  <div className="card border-left-success shadow h-100 py-2">
                    <div className="card-body">
                      <div className="row no-gutters align-items-center">
                        <div className="col mr-2">
                          <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                            Subscription Revenue
                          </div>
                          <div className="text-xs text-muted mb-1">(Platform Revenue)</div>
                          <div className="h5 mb-0 font-weight-bold text-gray-800">
                            ₹{overviewData.subscriptionRevenue.toLocaleString()}
                          </div>
                        </div>
                        <div className="col-auto">
                          <FontAwesomeIcon icon={faUsers} className="fa-2x text-gray-300" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-md-6 mb-4">
                  <div className="card border-left-warning shadow h-100 py-2">
                    <div className="card-body">
                      <div className="row no-gutters align-items-center">
                        <div className="col mr-2">
                          <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                            Session Revenue
                          </div>
                          <div className="text-xs text-muted mb-1">(Held for Mentors)</div>
                          <div className="h5 mb-0 font-weight-bold text-gray-800">
                            ₹{overviewData.sessionRevenue.toLocaleString()}
                          </div>
                        </div>
                        <div className="col-auto">
                          <FontAwesomeIcon icon={faCalendarAlt} className="fa-2x text-gray-300" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-md-6 mb-4">
                  <div className="card border-left-info shadow h-100 py-2">
                    <div className="card-body">
                      <div className="row no-gutters align-items-center">
                        <div className="col mr-2">
                          <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                            Active Subscriptions
                          </div>
                          <div className="h5 mb-0 font-weight-bold text-gray-800">
                            {overviewData.activeSubscriptions.toLocaleString()}
                          </div>
                        </div>
                        <div className="col-auto">
                          <FontAwesomeIcon icon={faUsers} className="fa-2x text-gray-300" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Monthly Revenue Graph */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="m-0 font-weight-bold text-primary">Monthly Revenue Trend</h6>
                    </div>
                    <div className="card-body">
                      <div className="chart-container" style={{ position: 'relative', height: '300px' }}>
                        <canvas id="revenueChart"></canvas>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === "payments" && (
            <div>
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="m-0 font-weight-bold text-primary">Explorer Payments</h6>
                  <div>
                    <button 
                      className="btn btn-sm btn-success mr-2"
                      onClick={exportToExcel}
                      disabled={payments.length === 0}
                    >
                      <FontAwesomeIcon icon={faDownload} className="mr-1" />
                      Export to Excel
                    </button>
                    <button className="btn btn-sm btn-primary">
                      <FontAwesomeIcon icon={faEye} className="mr-1" />
                      View All
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-bordered" width="100%" cellSpacing="0">
                      <thead>
                        <tr>
                          <th>Payment ID</th>
                          <th>Mentor</th>
                          <th>Mentee</th>
                          <th>Session Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((payment, index) => (
                          <tr key={payment.id || index}>
                            <td>{payment.id || payment.payment_id || `PAY-${index + 1}`}</td>
                            <td>
                              <div>
                                <div className="font-weight-bold">
                                  {payment.mentor || payment.mentorName || payment.mentor_name || payment.expertName || payment.expert_name || 'N/A'}
                                </div>
                                <small className="text-muted">
                                  Acc: {payment.mentorAccountNumber || payment.mentor_account_number || 'N/A'}
                                </small>
                              </div>
                            </td>
                            <td>
                              <div>
                                <div className="font-weight-bold">
                                  {payment.mentee || payment.menteeName || payment.mentee_name || payment.userName || payment.user_name || 'N/A'}
                                </div>
                                <small className="text-muted">
                                  Acc: {payment.menteeAccountNumber || payment.mentee_account_number || 'N/A'}
                                </small>
                              </div>
                            </td>
                            <td>
                              {payment.session_start || payment.sessionStart || payment.sessionDate || payment.session_date || payment.appointmentDate || payment.appointment_date || payment.created_at || 'N/A'}
                            </td>
                            <td>₹{payment.amount || payment.gross_price || 0}</td>
                            <td>
                              <span className={`badge badge-${getStatusBadgeColor(payment.payment_status || payment.status)}`}>
                                {getStatusText(payment.payment_status || payment.status)}
                              </span>
                              {(payment.payment_status === 'cancelled' || payment.status === 'cancelled') && (
                                <div className="mt-1">
                                  <small className="text-danger">
                                    {payment.cancellationReason || payment.cancel_reason || payment.reason || 'No reason provided'}
                                  </small>
                                </div>
                              )}
                              {(payment.payment_status === 'no_show' || payment.status === 'no_show') && (
                                <div className="mt-1">
                                  <small className="text-warning">
                                    No show by: {getNoShowBy(payment)}
                                  </small>
                                </div>
                              )}
                            </td>
                            <td>
                              <button className="btn btn-sm btn-outline-primary mr-1">
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                              <button className="btn btn-sm btn-outline-success mr-1">
                                <FontAwesomeIcon icon={faCheckCircle} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payouts Tab */}
          {activeTab === "payouts" && (
            <div>
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="m-0 font-weight-bold text-primary">Expert Payouts</h6>
                  <div>
                    <button 
                      className="btn btn-sm btn-success mr-2"
                      onClick={exportPayoutsToExcel}
                      disabled={payouts.length === 0}
                    >
                      <FontAwesomeIcon icon={faDownload} className="mr-1" />
                      Export to Excel
                    </button>
                    <button className="btn btn-sm btn-primary" disabled>
                      <FontAwesomeIcon icon={faPlay} className="mr-1" />
                      Trigger Payout
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-bordered" width="100%" cellSpacing="0">
                      <thead>
                        <tr>
                          <th>Mentor</th>
                          <th>Total Sessions</th>
                          <th>Total Amount</th>
                          <th>Payout Amount</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payouts.map((payout, index) => (
                          <tr key={payout.id || index}>
                            <td>
                              <div>
                                <div className="font-weight-bold">
                                  {payout.mentorName || payout.mentor_name || payout.expertName || payout.expert_name || 'N/A'}
                                </div>
                                <small className="text-muted">
                                  Acc: {payout.mentorAccountNumber || payout.mentor_account_number || 'N/A'}
                                </small>
                                <br />
                                <small className="text-info">
                                  {payout.paymentMethod || payout.payment_method || 'N/A'}
                                </small>
                              </div>
                            </td>
                            <td>{payout.totalSessions || payout.total_sessions || 0}</td>
                            <td>₹{payout.totalAmount || payout.total_amount || 0}</td>
                            <td className="font-weight-bold text-success">
                              ₹{payout.payoutAmount || payout.payout_amount || 0}
                            </td>
                            <td>
                              <span className={`badge badge-${getPayoutStatusBadgeColor(payout.payout_status || payout.status)}`}>
                                {getPayoutStatus(payout.payout_status || payout.status)}
                              </span>
                            </td>
                            <td>
                              <button className="btn btn-sm btn-outline-primary mr-1" disabled>
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                              <button className="btn btn-sm btn-outline-success" disabled>
                                <FontAwesomeIcon icon={faPlay} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Refunds Tab */}
          {activeTab === "refunds" && (
            <div>
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="m-0 font-weight-bold text-primary">Refunds</h6>
                  <div>
                    <button 
                      className="btn btn-sm btn-success mr-2"
                      onClick={exportRefundsToExcel}
                      disabled={refunds.length === 0}
                    >
                      <FontAwesomeIcon icon={faDownload} className="mr-1" />
                      Export to Excel
                    </button>
                    <button className="btn btn-sm btn-warning">
                      <FontAwesomeIcon icon={faUndo} className="mr-1" />
                      Issue Refund
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-bordered" width="100%" cellSpacing="0">
                      <thead>
                        <tr>
                          <th>Refund ID</th>
                          <th>Mentor</th>
                          <th>Mentee</th>
                          <th>Original Payment</th>
                          <th>Refund Amount</th>
                          <th>Reason</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {refunds.map((refund, index) => (
                          <tr key={refund.id || index}>
                            <td>{refund.id || refund.refund_id || `REF-${index + 1}`}</td>
                            <td>
                              <div>
                                <div className="font-weight-bold">
                                  {refund.mentorName || refund.mentor_name || refund.expertName || refund.expert_name || 'N/A'}
                                </div>
                                <small className="text-muted">
                                  Acc: {refund.mentorAccountNumber || refund.mentor_account_number || 'N/A'}
                                </small>
                              </div>
                            </td>
                            <td>
                              <div>
                                <div className="font-weight-bold">
                                  {refund.menteeName || refund.mentee_name || refund.userName || refund.user_name || 'N/A'}
                                </div>
                                <small className="text-muted">
                                  Acc: {refund.menteeAccountNumber || refund.mentee_account_number || 'N/A'}
                                </small>
                              </div>
                            </td>
                            <td>
                              <div>
                                <div>#{refund.paymentId || refund.payment_id || 'N/A'}</div>
                                <small className="text-muted">
                                  {refund.sessionDate || refund.session_date || 'N/A'}
                                </small>
                              </div>
                            </td>
                            <td className="font-weight-bold text-danger">
                              ₹{refund.refundAmount || refund.refund_amount || 0}
                            </td>
                            <td>
                              <div>
                                <div>{refund.refundReason || refund.refund_reason || refund.reason || 'N/A'}</div>
                                {(refund.refundReason === 'no_show' || refund.reason === 'no_show') && (
                                  <small className="text-warning">
                                    No show by: {getNoShowBy(refund)}
                                  </small>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className={`badge badge-${getRefundStatusBadgeColor(refund.refund_status || refund.status)}`}>
                                {getRefundStatus(refund.refund_status || refund.status)}
                              </span>
                            </td>
                            <td>
                              <button className="btn btn-sm btn-outline-primary mr-1">
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                              <button className="btn btn-sm btn-outline-warning">
                                <FontAwesomeIcon icon={faUndo} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Payments; 